import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import logsService from '../../services/logsService';

const initialState = {
  logs: [],
  currentLog: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  loading: false,
  error: null,
};

export const fetchLogs = createAsyncThunk(
  'logs/fetchLogs',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await logsService.getLogs(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch logs');
    }
  }
);

export const fetchLogById = createAsyncThunk(
  'logs/fetchLogById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await logsService.getLogById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch log');
    }
  }
);

export const createLog = createAsyncThunk(
  'logs/createLog',
  async (logData, { rejectWithValue }) => {
    try {
      const response = await logsService.createLog(logData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create log');
    }
  }
);

export const updateLog = createAsyncThunk(
  'logs/updateLog',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await logsService.updateLog(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update log');
    }
  }
);

export const submitLog = createAsyncThunk(
  'logs/submitLog',
  async (id, { rejectWithValue }) => {
    try {
      const response = await logsService.submitLog(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to submit log');
    }
  }
);

export const deleteLog = createAsyncThunk(
  'logs/deleteLog',
  async (id, { rejectWithValue }) => {
    try {
      await logsService.deleteLog(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete log');
    }
  }
);

const logsSlice = createSlice({
  name: 'logs',
  initialState,
  reducers: {
    clearCurrentLog: (state) => {
      state.currentLog = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch logs
      .addCase(fetchLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch log by ID
      .addCase(fetchLogById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLogById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLog = action.payload;
      })
      .addCase(fetchLogById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create log
      .addCase(createLog.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLog.fulfilled, (state, action) => {
        state.loading = false;
        state.logs.unshift(action.payload);
      })
      .addCase(createLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update log
      .addCase(updateLog.fulfilled, (state, action) => {
        const index = state.logs.findIndex((log) => log.id === action.payload.id);
        if (index !== -1) {
          state.logs[index] = action.payload;
        }
        if (state.currentLog?.id === action.payload.id) {
          state.currentLog = action.payload;
        }
      })
      // Submit log
      .addCase(submitLog.fulfilled, (state, action) => {
        const index = state.logs.findIndex((log) => log.id === action.payload.id);
        if (index !== -1) {
          state.logs[index] = action.payload;
        }
      })
      // Delete log
      .addCase(deleteLog.fulfilled, (state, action) => {
        state.logs = state.logs.filter((log) => log.id !== action.payload);
      });
  },
});

export const { clearCurrentLog, clearError } = logsSlice.actions;
export default logsSlice.reducer;
